import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlertTriangle, ArrowLeft, ArrowRight, Check, Copy, Loader2, Pencil, RotateCcw, Scale, ShieldAlert, X,
} from 'lucide-react';
import { apiFetch } from '@/lib/api-client';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import {
  AERIAL_ANCHOR_DISTANCE_M, DELIVERY, DEPLOYMENT, NETWORK, QUOTE_DISTANCE_M, TUTORIAL_PHASES,
  blockedDeliveryReason, newDeliveryCode, quadrantFor,
} from '@uniticket/shared';
import type {
  TutorialAnswer, TutorialAnswerMatch, TutorialCard, TutorialCodeGroup, TutorialNodeDto, TutorialPhase,
  TutorialScopeOutcome, TutorialTag,
} from '@uniticket/shared';
import { Rich, RichBlock } from './rich-text';
import { CardEditor, ControlPanel, computeAnomalies } from './TutorialCardEditor';

// Onglet « Tutoriel » — le PARCOURS GUIDÉ (N32.6, plan validé le 29-09-2026), sur la maquette « Parcours d'un
// ticket » (v2 du 24-09) que le user a fait valider en réunion.
//
// L'HISTOIRE DE CET ÉCRAN, parce qu'elle explique ses choix. Le 22-09 au soir, la vue était passée d'un parcours
// pas à pas à un ARBRE DÉPLIABLE, pour une raison qui tient toujours : « un parcours pas à pas cache la carte, et
// Anouk veut un pense-bête pour des gens qui reviennent une fois par semaine ». Le 24-09, la maquette a montré
// qu'on pouvait avoir les deux ; le 29-09, le user en a fait l'écran. D'où ce qui suit :
//  ① UNE CARTE À LA FOIS, avec le fil des réponses au-dessus (cliquable : on revient à n'importe quelle question) ;
//  ② LA CARTE ENTIÈRE reste à un clic : le rail des six phases montre le chemin parcouru, et « Tout voir » y déplie
//     toutes les étapes de chaque phase, cliquables. Une seule vue sur la donnée, pas deux à maintenir.
//  ③ Une issue NON CÂBLÉE reste visible, désactivée, et le dit (invariant ② du service) : un parcours en cours
//     d'écriture doit se voir.
//
// LE CONTENU EST EN BASE (tt_tutorial_nodes, colonne `card`) et les admins le corrigent carte par carte, à
// l'écran. Ce que la maquette codait en dur est devenu de la donnée : la carte « code » choisit la suite selon la
// CORRESPONDANCE de ses réponses (groupe du code saisi), la carte « scope » aussi (issue des trois cases), et
// toute autre carte SUGGÈRE la réponse que le code saisi désigne (cadran, client connecté, code de blocage).
// Les tables de décodage et les seuils de distance sont ceux de l'application (@uniticket/shared) — jamais
// recopiés : le tutoriel explique la règle que l'écran applique.
//
// Les libellés suivent le parti pris de la page qui l'héberge (Documentation.tsx) : une constante bilingue FR/EN
// plutôt que des dizaines de clés i18n. Le CONTENU, lui, n'est pas traduit — c'est de la donnée écrite par le
// métier, dans sa langue.

type Lang = 'en' | 'fr';

const L = {
  fr: {
    lead: 'Traitez un ticket étape par étape : répondez à chaque question, la suite s’affiche. Le fil de vos réponses permet de revenir en arrière, et « Tout voir » montre toutes les étapes.',
    restart: 'Recommencer',
    rules: 'Règles de capacité',
    railTitle: 'Le parcours',
    showAll: 'Tout voir',
    showPath: 'Mon parcours',
    factsTitle: 'Ce que vous avez relevé',
    factsEmpty: 'Rien pour l’instant : le code, le scope et le cadran s’afficheront ici.',
    legendTitle: 'Légende',
    trailEmpty: 'Vos réponses s’afficheront ici. Cliquez sur l’une d’elles pour revenir à cette question.',
    trailDirect: 'Étape ouverte directement — le fil repart d’ici.',
    backTo: 'Revenir à :',
    stepOf: 'Étape {n} sur {total}',
    back: 'Étape précédente',
    nextTicket: 'Ticket suivant',
    continue: 'Continuer',
    more: 'En savoir plus',
    comment: 'Réponse type',
    copy: 'Copier',
    copied: 'Copié',
    copyError: 'La copie a échoué.',
    reference: 'Règle de référence',
    notWired: 'issue pas encore câblée',
    missingTarget: 'cette issue mène à une étape qui n’existe plus',
    suggested: 'suggéré',
    byCode: 'd’après le code',
    byCodeTitle: 'Étape passée d’office : le code de livraison saisi désignait cette réponse.',
    suggestion: 'D’après le code {code} : {group}{quadrant}.',
    quadrant: ', cadran {q}',
    optional: 'facultatif',
    keys: 'Clavier : 1 à 9 choisissent une réponse · ← revient à l’étape précédente · Entrée valide un code',
    loading: 'Chargement du parcours…',
    loadError: 'Le parcours n’a pas pu être chargé.',
    empty: 'Aucune étape pour l’instant.',
    noRoot: 'Aucune entrée : cochez « entrée » sur au moins une étape.',
    close: 'Fermer',
    codeLabel: 'Code de livraison',
    examples: 'Exemples :',
    noCode: 'Je n’ai pas le code sous les yeux',
    decodeEmpty: 'Tapez les quatre chiffres, ou cliquez sur un exemple.',
    decodePartial: 'Un code de livraison a quatre chiffres.',
    decodeUnknown: 'Code {code} inconnu : vérifiez la saisie. Si la fiche ne sait pas le décoder non plus, remontez le cas.',
    segPose: 'Type de pose',
    segNet: 'Réseau, donc cadran',
    segStatus: 'Statut',
    flatSeries: 'Série plate : une seule valeur',
    blockedUnknown: 'code de blocage absent des tables connues',
    noRoute: 'Aucune réponse de cette carte ne correspond : choisissez vous-même ci-dessous.',
    scopeV2: 'Build list (V2)',
    scopeV2Hint: 'construite chez nous',
    scopeV15: 'Deployment list (V1.5)',
    scopeV15Hint: 'annoncée à Proximus',
    scopeOos: 'Out of scope',
    scopeOosHint: 'une date de retrait est posée',
    chooseMyself: 'Je préfère choisir moi-même',
    distanceTitle: 'Vérifier la distance',
    distanceLabel: 'Distance mesurée',
    pose7: 'Souterrain',
    pose8: 'Façade',
    pose9: 'Aérien',
    distNoPose: 'Choisissez le type de pose.',
    distNoValue: 'Tapez la distance mesurée, en mètres.',
    distNegative: 'Une distance ne peut pas être négative.',
    distQuote: 'Au-delà de {m} m en souterrain : un devis sera exigé au release.',
    distAnchor: 'Au-delà de {m} m en aérien : il faudra un point d’ancrage intermédiaire, sinon le câble pend.',
    distOk: 'Pas de contrainte de distance pour ce type de pose.',
    factCode: 'Code',
    factDistance: 'Distance',
    edit: 'Modifier cette étape',
    control: 'Contrôle',
  },
  en: {
    lead: 'Handle a ticket step by step: answer each question and the next one appears. The trail of your answers lets you go back, and “Show all” lists every step.',
    restart: 'Start over',
    rules: 'Capacity rules',
    railTitle: 'The path',
    showAll: 'Show all',
    showPath: 'My path',
    factsTitle: 'What you noted',
    factsEmpty: 'Nothing yet: the code, the scope and the quadrant will show up here.',
    legendTitle: 'Legend',
    trailEmpty: 'Your answers will show up here. Click one to go back to that question.',
    trailDirect: 'Step opened directly — the trail starts from here.',
    backTo: 'Back to:',
    stepOf: 'Step {n} of {total}',
    back: 'Previous step',
    nextTicket: 'Next ticket',
    continue: 'Continue',
    more: 'Learn more',
    comment: 'Ready-made reply',
    copy: 'Copy',
    copied: 'Copied',
    copyError: 'Copy failed.',
    reference: 'Reference rule',
    notWired: 'outcome not wired yet',
    missingTarget: 'this outcome leads to a step that no longer exists',
    suggested: 'suggested',
    byCode: 'from the code',
    byCodeTitle: 'Step skipped automatically: the delivery code entered pointed to this answer.',
    suggestion: 'From code {code}: {group}{quadrant}.',
    quadrant: ', quadrant {q}',
    optional: 'optional',
    keys: 'Keyboard: 1 to 9 pick an answer · ← goes back one step · Enter confirms a code',
    loading: 'Loading the path…',
    loadError: 'The path could not be loaded.',
    empty: 'No step yet.',
    noRoot: 'No entry: tick “entry” on at least one step.',
    close: 'Close',
    codeLabel: 'Delivery code',
    examples: 'Examples:',
    noCode: 'I don’t have the code at hand',
    decodeEmpty: 'Type the four digits, or click an example.',
    decodePartial: 'A delivery code has four digits.',
    decodeUnknown: 'Unknown code {code}: check what you typed. If the reference card cannot decode it either, escalate.',
    segPose: 'Deployment',
    segNet: 'Network, hence quadrant',
    segStatus: 'Status',
    flatSeries: 'Flat series: a single value',
    blockedUnknown: 'blocking code missing from the known tables',
    noRoute: 'No answer of this card matches: pick one yourself below.',
    scopeV2: 'Build list (V2)',
    scopeV2Hint: 'built by us',
    scopeV15: 'Deployment list (V1.5)',
    scopeV15Hint: 'announced to Proximus',
    scopeOos: 'Out of scope',
    scopeOosHint: 'a removal date is set',
    chooseMyself: 'I’d rather choose myself',
    distanceTitle: 'Check the distance',
    distanceLabel: 'Measured distance',
    pose7: 'Underground',
    pose8: 'Façade',
    pose9: 'Aerial',
    distNoPose: 'Pick the deployment type.',
    distNoValue: 'Type the measured distance, in metres.',
    distNegative: 'A distance cannot be negative.',
    distQuote: 'Beyond {m} m underground: a quote will be required at release.',
    distAnchor: 'Beyond {m} m aerial: an intermediate anchor point is needed, otherwise the cable sags.',
    distOk: 'No distance constraint for this deployment type.',
    factCode: 'Code',
    factDistance: 'Distance',
    edit: 'Edit this step',
    control: 'Check',
  },
} as const;

// Les deux langues portent les mêmes clés, mais `as const` leur donne des types littéraux distincts : sans cet
// élargissement, passer `lbl` à un enfant ne compilerait pas (union de deux objets figés).
type Labels = { [K in keyof typeof L['fr']]: string };
const fill = (s: string, v: Record<string, string | number>) => s.replace(/\{(\w+)\}/g, (_, k) => String(v[k] ?? ''));

const PHASE_LABEL: Record<Lang, Record<TutorialPhase, string>> = {
  fr: { lire: 'Lire le ticket', identifier: 'Identifier l’adresse', verifier: 'Vérifier l’adresse', capacite: 'La capacité', agir: 'Agir', terminer: 'Terminer' },
  en: { lire: 'Read the ticket', identifier: 'Identify the address', verifier: 'Check the address', capacite: 'Capacity', agir: 'Act', terminer: 'Finish' },
};
const TAG_LABEL: Record<Lang, Record<TutorialTag, [string, string]>> = {
  fr: {
    todo: ['À définir', 'aucune règle écrite pour l’instant'],
    decide: ['À trancher', 'deux sources se contredisent'],
    meeting: ['Retour du 24-09', 'ajouté ou changé après la réunion'],
    future: ['Pas encore codé', 'décidé, pas encore dans l’application'],
  },
  en: {
    todo: ['To be defined', 'no written rule yet'],
    decide: ['To be settled', 'two sources contradict each other'],
    meeting: ['24-09 feedback', 'added or changed after the meeting'],
    future: ['Not coded yet', 'decided, not in the app yet'],
  },
};
// ⚠ L'ambre dit UNE chose dans cet écran : « à définir ». Il n'est donné à rien d'autre (leçon du 22-09 : une
// couleur qui veut dire deux choses ne veut plus rien dire).
const TAG_CLASS: Record<TutorialTag, string> = {
  todo: 'border-amber-300 bg-amber-50 text-amber-800',
  decide: 'border-orange-300 bg-orange-50 text-orange-800',
  meeting: 'border-fuchsia-300 bg-fuchsia-50 text-fuchsia-800',
  future: 'border-sky-300 bg-sky-50 text-sky-800',
};
const CALLOUT_CLASS: Record<string, string> = {
  todo: 'border-amber-300 bg-amber-50 text-amber-900',
  decide: 'border-orange-300 bg-orange-50 text-orange-900',
  warn: 'border-rose-300 bg-rose-50 text-rose-900',
  ok: 'border-emerald-300 bg-emerald-50 text-emerald-900',
};
const CALLOUT_PREFIX: Record<Lang, Record<string, string>> = {
  fr: { todo: 'À définir —', decide: 'À trancher —', warn: 'Attention —', ok: '' },
  en: { todo: 'To be defined —', decide: 'To be settled —', warn: 'Watch out —', ok: '' },
};
const GROUP_LABEL: Record<Lang, Record<TutorialCodeGroup, string>> = {
  fr: { HP_HCA: 'Homes Passed', NOT_HP: 'pas encore Homes Passed', HC: 'Homes Connect', BLOCKED: 'code de blocage', NONE: 'aucun code' },
  en: { HP_HCA: 'Homes Passed', NOT_HP: 'not Homes Passed yet', HC: 'Homes Connect', BLOCKED: 'blocking code', NONE: 'no code' },
};
const GROUP_VERDICT: Record<Lang, Record<Exclude<TutorialCodeGroup, 'NONE'>, [string, string]>> = {
  fr: {
    HP_HCA: ['ok', 'Homes Passed : l’adresse est raccordée au réseau.'],
    NOT_HP: ['todo', 'Pas encore Homes Passed : le réseau n’est pas encore raccordé.'],
    HC: ['info', 'Homes Connect : un client est déjà connecté sur cette unité.'],
    BLOCKED: ['bad', 'Code de blocage : l’adresse n’est pas connectable en l’état.'],
  },
  en: {
    HP_HCA: ['ok', 'Homes Passed: the address is connected to the network.'],
    NOT_HP: ['todo', 'Not Homes Passed yet: the network is not connected yet.'],
    HC: ['info', 'Homes Connect: a customer is already connected on this unit.'],
    BLOCKED: ['bad', 'Blocking code: the address cannot be connected as it is.'],
  },
};
const VERDICT_CLASS: Record<string, string> = {
  ok: 'border-emerald-300 bg-emerald-50 text-emerald-900',
  todo: 'border-amber-300 bg-amber-50 text-amber-900',
  info: 'border-sky-300 bg-sky-50 text-sky-900',
  bad: 'border-rose-300 bg-rose-50 text-rose-900',
  neutral: 'border-slate-200 bg-slate-50 text-slate-600',
};

// ── Le code de livraison, décodé avec les tables de l'application ─────────────────────────────────────
export type DecodedCode =
  | { state: 'empty' } | { state: 'partial' } | { state: 'unknown'; code: number }
  | {
      state: 'ok'; code: number; group: Exclude<TutorialCodeGroup, 'NONE'>; quadrant: string | null;
      shape: 'composite' | 'flat' | 'blocked';
      digits?: { pose: string; poseLabel: string; net: string; netLabel: string; status: string; statusLabel: string };
      label?: string; family?: string; reason?: string | null;
    };

/** Le même ordre que `decodeDeliveryStatus` : un code de blocage d'abord (sa raison exacte, jamais une lecture
 *  chiffre par chiffre), puis la série plate (1108 est UNE valeur), puis le code composé. */
export function classifyDeliveryCode(raw: string): DecodedCode {
  const s = raw.trim();
  if (s === '') return { state: 'empty' };
  if (!/^\d{4}$/.test(s)) return { state: 'partial' };
  const code = Number(s);
  const blocked = blockedDeliveryReason(code);
  if (blocked) return { state: 'ok', code, group: 'BLOCKED', quadrant: null, shape: 'blocked', family: blocked.family, reason: blocked.reason };
  const neu = newDeliveryCode(code);
  if (neu) {
    return { state: 'ok', code, group: neu.group === 'HP_WIP' ? 'NOT_HP' : neu.group, quadrant: neu.quadrant, shape: 'flat', label: neu.label };
  }
  const d = s[0]; const net = s.slice(1, 3); const st = s[3];
  if (d === '2') return { state: 'ok', code, group: 'BLOCKED', quadrant: null, shape: 'blocked', family: DEPLOYMENT['2'], reason: null };
  if (DEPLOYMENT[d] && NETWORK[net] && DELIVERY[st]) {
    return {
      state: 'ok', code, group: st === '5' ? 'HP_HCA' : st === '6' ? 'HC' : 'NOT_HP', quadrant: quadrantFor(code), shape: 'composite',
      digits: { pose: d, poseLabel: DEPLOYMENT[d], net, netLabel: NETWORK[net], status: st, statusLabel: DELIVERY[st] },
    };
  }
  return { state: 'unknown', code };
}

type ScopeState = { v2: boolean; v15: boolean; oos: boolean };
function scopeOutcome(s: ScopeState): TutorialScopeOutcome {
  if (s.oos) return 'oos';
  if (s.v2 && s.v15) return 'both';
  if (s.v2) return 'v2';
  if (s.v15) return 'v15';
  return 'none';
}

/** Une correspondance est satisfaite quand CHAQUE critère présent l'est. Sans aucun critère utilisable, elle ne
 *  désigne rien — une réponse sans correspondance n'est jamais suggérée. */
function matches(m: TutorialAnswerMatch | null | undefined, code: DecodedCode, scope?: TutorialScopeOutcome): boolean {
  if (!m) return false;
  let used = false;
  if (m.groups?.length) {
    used = true;
    const g: TutorialCodeGroup = code.state === 'ok' ? code.group : 'NONE';
    if (!m.groups.includes(g)) return false;
  }
  if (m.quadrants?.length) {
    used = true;
    if (code.state !== 'ok' || !code.quadrant || !m.quadrants.includes(code.quadrant)) return false;
  }
  if (m.codes?.length) {
    used = true;
    if (code.state !== 'ok' || !m.codes.includes(code.code)) return false;
  }
  if (m.scope?.length) {
    used = true;
    if (!scope || !m.scope.includes(scope)) return false;
  }
  return used;
}
/** Une réponse d'une carte ordinaire correspond-elle au code saisi ? Seulement d'après les critères du code
 *  (groupe, cadran, code exact) — jamais d'après le scope, qui a sa propre carte. */
function answerMatchesCode(a: TutorialAnswer, code: DecodedCode): boolean {
  const m = a.match;
  if (!m || !(m.groups?.length || m.quadrants?.length || m.codes?.length)) return false;
  return matches({ groups: m.groups, quadrants: m.quadrants, codes: m.codes }, code);
}
/** La réponse que le code saisi désigne, sur une carte ordinaire : seulement si un code est décodé. La PREMIÈRE
 *  qui correspond — c'est une suggestion, le relecteur choisit. */
function suggestedIndex(answers: TutorialAnswer[], code: DecodedCode): number {
  if (code.state !== 'ok') return -1;
  return answers.findIndex((a) => answerMatchesCode(a, code));
}
/**
 * N32.7 (décisions du user des 07-10 et 08-10) — la réponse que le code TRANCHE sur une carte « à passer
 * d'office » : la carte porte la case, un code est décodé, et UNE SEULE de ses réponses lui correspond. Deux
 * réponses possibles (un code en D quand « D » et « Dx » portent le même critère), aucune, pas de code, ou une
 * carte qui a son propre aiguillage : `null`, et la carte s'affiche comme toujours. « Une seule » est plus strict
 * que la suggestion ci-dessus, à dessein : suggérer la première ne coûte rien, CHOISIR à la place du relecteur
 * demande qu'il n'y ait pas de choix.
 */
function decidedAnswer(node: TutorialNodeDto | undefined, code: DecodedCode): TutorialAnswer | null {
  if (!node || node.card.skipWhenCodeDecides !== true || code.state !== 'ok') return null;
  const kind = node.card.kind;
  if (kind === 'code' || kind === 'scope' || kind === 'window' || kind === 'rules') return null;
  const hits = node.answers.filter((a) => answerMatchesCode(a, code));
  return hits.length === 1 ? hits[0] : null;
}

type Pose = '' | '7' | '8' | '9';
interface Ctx {
  code: string;
  scope: ScopeState;
  checks: Record<string, Record<string, boolean>>;
  pose: Pose;
  distance: string;
}
const freshCtx = (): Ctx => ({ code: '', scope: { v2: false, v15: false, oos: false }, checks: {}, pose: '', distance: '' });

/** Une réponse du fil : son étiquette, et si c'est le CODE qui l'a prise à la place du relecteur (N32.7 — la carte
 *  a été passée d'office). Le fil et « ce que vous avez relevé » la montrent comme les autres, avec sa marque. */
interface Choice {
  chip: string;
  auto: boolean;
}

function distanceVerdict(ctx: Ctx, lbl: Labels): [string, string] {
  if (!ctx.pose) return ['neutral', lbl.distNoPose];
  const raw = ctx.distance.replace(',', '.').trim();
  const m = Number.parseFloat(raw);
  if (raw === '' || Number.isNaN(m)) return ['neutral', lbl.distNoValue];
  if (m < 0) return ['bad', lbl.distNegative];
  if (ctx.pose === '7' && m > QUOTE_DISTANCE_M) return ['todo', fill(lbl.distQuote, { m: QUOTE_DISTANCE_M })];
  if (ctx.pose === '9' && m > AERIAL_ANCHOR_DISTANCE_M) return ['info', fill(lbl.distAnchor, { m: AERIAL_ANCHOR_DISTANCE_M })];
  return ['ok', lbl.distOk];
}

function phaseOf(node: TutorialNodeDto | undefined): TutorialPhase {
  const p = node?.category;
  return (TUTORIAL_PHASES as readonly string[]).includes(p ?? '') ? (p as TutorialPhase) : 'lire';
}
const phaseIndex = (p: TutorialPhase) => TUTORIAL_PHASES.indexOf(p);
const shortOf = (n: TutorialNodeDto) => n.card.short || n.title;
const chipOf = (a: TutorialAnswer) => a.chip || a.label.replace(/\*\*|`/g, '');

// ── L'écran ───────────────────────────────────────────────────────────────────────────────────────────

export function Tutorial() {
  const { i18n } = useTranslation();
  const lang: Lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').startsWith('fr') ? 'fr' : 'en';
  const lbl: Labels = L[lang];
  const { isAdmin } = useAuth();

  const [nodes, setNodes] = useState<TutorialNodeDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [path, setPath] = useState<string[]>([]);
  const [choices, setChoices] = useState<Choice[]>([]);
  const [ctx, setCtx] = useState<Ctx>(freshCtx);
  const [showAll, setShowAll] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [dir, setDir] = useState<'fwd' | 'back' | null>(null);
  // Admins : la carte en cours d'édition (une seule à la fois), et le panneau « Contrôle ».
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [controlOpen, setControlOpen] = useState(false);
  const cardRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch<{ data: TutorialNodeDto[] }>('/tutorial/nodes');
      setNodes(res.data);
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const byKey = useMemo(() => new Map(nodes.map((n) => [n.key, n])), [nodes]);
  const roots = useMemo(
    () => nodes.filter((n) => n.isRoot && n.card.kind !== 'rules')
      .sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title)),
    [nodes],
  );
  const rulesNode = useMemo(() => nodes.find((n) => n.card.kind === 'rules') ?? null, [nodes]);
  const entry = roots[0]?.key ?? null;

  // Le chemin repart de l'entrée quand il est vide, ou quand une de ses étapes a disparu (suppression, renommage
  // par un admin dans un autre onglet) : un fil qui désigne une étape absente ne mène nulle part.
  useEffect(() => {
    if (!entry) return;
    if (path.length === 0 || path.some((k) => !byKey.has(k))) {
      setPath([entry]);
      setChoices([]);
    }
  }, [entry, byKey, path]);

  const currentKey = path[path.length - 1];
  const current = currentKey ? byKey.get(currentKey) : undefined;
  const code = useMemo(() => classifyDeliveryCode(ctx.code), [ctx.code]);
  const anomalies = useMemo(() => (isAdmin ? computeAnomalies(nodes, lang) : []), [isAdmin, nodes, lang]);

  // Quitter la carte ferme son édition : on ne corrige que ce qu'on a sous les yeux.
  useEffect(() => { setEditingKey(null); }, [currentKey]);

  /** Après un enregistrement ou une suppression : l'arbre relu ET le chemin corrigé dans le MÊME rendu. Un
   *  renommage garde le lecteur sur la carte (l'ancienne clé est remplacée par la nouvelle dans le chemin) ; une
   *  suppression le ramène à l'étape d'avant. Faits séparément, la garde « chemin vers une étape absente »
   *  renverrait au départ entre les deux. */
  const onSaved = useCallback(async (oldKey: string, newKey: string) => {
    const res = await apiFetch<{ data: TutorialNodeDto[] }>('/tutorial/nodes');
    setNodes(res.data);
    setPath((p) => p.map((k) => (k === oldKey ? newKey : k)));
    setEditingKey(null);
  }, []);
  const onDeleted = useCallback(async (key: string) => {
    const res = await apiFetch<{ data: TutorialNodeDto[] }>('/tutorial/nodes');
    const i = path.indexOf(key);
    setNodes(res.data);
    if (i > 0) { setPath(path.slice(0, i)); setChoices(choices.slice(0, i - 1)); }
    setEditingKey(null);
  }, [path, choices]);

  const go = useCallback((to: string | null | undefined, chip: string) => {
    if (!to || !byKey.has(to)) return;
    // N32.7 — AVANCER peut passer d'office une carte que le code tranche : on l'empile quand même, avec la réponse
    // que le code désigne, puis sa suite — le fil garde l'étape (marquée « d'après le code »), et ← y ramène. Seul
    // ce pas-ci enclenche le saut : reculer, sauter dans le fil, recommencer ou ouvrir une étape depuis le rail
    // affichent la carte. `passed` empêche une carte de se ré-enclencher dans la même avancée (deux cartes cochées
    // qui se renvoient l'une à l'autre tourneraient sans fin).
    const steps: string[] = [to];
    const picked: Choice[] = [{ chip, auto: false }];
    const passed = new Set<string>();
    for (;;) {
      const here = byKey.get(steps[steps.length - 1]);
      if (!here || passed.has(here.key)) break;
      const answer = decidedAnswer(here, code);
      if (!answer?.targetKey || !byKey.has(answer.targetKey)) break;
      passed.add(here.key);
      picked.push({ chip: chipOf(answer), auto: true });
      steps.push(answer.targetKey);
    }
    setChoices((c) => [...c, ...picked]);
    setPath((p) => [...p, ...steps]);
    setDir('fwd');
  }, [byKey, code]);
  const back = useCallback(() => {
    setPath((p) => (p.length < 2 ? p : p.slice(0, -1)));
    setChoices((c) => c.slice(0, -1));
    setDir('back');
  }, []);
  const jump = useCallback((i: number) => {
    setPath((p) => p.slice(0, i + 1));
    setChoices((c) => c.slice(0, i));
    setDir('back');
  }, []);
  const restart = useCallback((from?: string) => {
    const start = from ?? entry;
    if (!start) return;
    setPath([start]);
    setChoices([]);
    setCtx(freshCtx());
    setDir('back');
  }, [entry]);
  const openDirect = useCallback((key: string) => {
    if (!byKey.has(key)) return;
    setPath([key]);
    setChoices([]);
    setDir('fwd');
  }, [byKey]);

  // Après chaque pas : le focus va au titre de la carte (lecteurs d'écran, clavier) — ou, sur la carte « code », au
  // champ du code, puisqu'on y arrive pour taper. La carte revient en vue si on l'avait fait défiler hors de
  // l'écran. Défilement « auto » : un onglet caché ne défile pas en « smooth ».
  const currentKind = current?.card.kind;
  useEffect(() => {
    if (!dir) return;
    const target = currentKind === 'code' ? document.getElementById('tuto-code') : titleRef.current;
    target?.focus({ preventScroll: true });
    const top = cardRef.current?.getBoundingClientRect().top ?? 0;
    if (top < 0) cardRef.current?.scrollIntoView({ block: 'start', behavior: 'auto' });
  }, [currentKey, currentKind, dir]);

  // Le clavier : 1 à 9 choisissent une réponse, ← revient en arrière. Jamais pendant une saisie, jamais sous la
  // fenêtre des règles, jamais avec une touche de modification (Ctrl+1 change d'onglet dans le navigateur).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Jamais pendant une édition : une flèche ← ferait quitter la carte et perdre ce qui est tapé.
      if (rulesOpen || editingKey || e.altKey || e.ctrlKey || e.metaKey) return;
      const t = e.target as HTMLElement | null;
      if (t?.closest('input, textarea, select, [contenteditable="true"]')) return;
      if (/^[1-9]$/.test(e.key)) {
        const b = cardRef.current?.querySelector<HTMLButtonElement>(`[data-k="${e.key}"]:not(:disabled)`);
        if (b) { e.preventDefault(); b.click(); }
      } else if (e.key === 'ArrowLeft' && path.length > 1) {
        e.preventDefault();
        back();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [rulesOpen, editingKey, path.length, back]);

  if (loading) {
    return (
      <p className="flex items-center gap-2 py-10 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> {lbl.loading}
      </p>
    );
  }
  if (failed) {
    return (
      <p className="mt-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
        <AlertTriangle className="h-4 w-4 shrink-0" /> {lbl.loadError}
      </p>
    );
  }
  if (nodes.length === 0) {
    return <p className="mt-6 rounded-lg border border-slate-200 bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">{lbl.empty}</p>;
  }
  if (!entry || !current) {
    return <p className="mt-6 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800">{lbl.noRoot}</p>;
  }

  const phase = phaseOf(current);
  const facts = factsOf(path, choices, byKey, code, ctx, lang, lbl);

  return (
    <div className="mt-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-2xl text-[13.5px] text-slate-600">{lbl.lead}</p>
        {/* Pas de shrink-0 : sur un écran étroit, les boutons passent à la ligne au lieu de déborder. */}
        <div className="flex flex-wrap gap-2">
          {/* Les entrées secondaires (« Je suis bloqué sur un point précis ») : un bouton chacune. */}
          {roots.slice(1).map((r) => (
            <Button key={r.key} variant="outline" size="sm" onClick={() => restart(r.key)}>{shortOf(r)}</Button>
          ))}
          {rulesNode && (
            <Button variant="outline" size="sm" onClick={() => setRulesOpen(true)}>
              <Scale className="mr-1.5 h-3.5 w-3.5" /> {lbl.rules}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => restart()}>
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> {lbl.restart}
          </Button>
          {/* Le bouton n'apparaît que pour un admin — et la garde qui compte est celle du serveur. */}
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setControlOpen((v) => !v)}
              className={anomalies.length ? 'border-amber-300 text-amber-800' : ''}
            >
              <ShieldAlert className="mr-1.5 h-3.5 w-3.5" /> {lbl.control} ({anomalies.length})
            </Button>
          )}
        </div>
      </div>

      {isAdmin && controlOpen && <ControlPanel nodes={nodes} lang={lang} onOpen={openDirect} />}

      <div className="grid gap-5 lg:grid-cols-[240px_minmax(0,1fr)]">
        <Rail
          nodes={nodes} path={path} current={current} showAll={showAll} onToggleAll={() => setShowAll((v) => !v)}
          onOpen={openDirect} facts={facts} lang={lang} lbl={lbl}
        />

        <main className="min-w-0 space-y-3">
          <MobileProgress phase={phase} facts={facts} lang={lang} lbl={lbl} />
          <Trail path={path} choices={choices} byKey={byKey} onJump={jump} lbl={lbl} />
          <section
            ref={cardRef}
            key={currentKey}
            data-tuto-card={currentKey}
            aria-live="polite"
            className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7 ${
              dir === 'fwd' ? 'animate-in fade-in slide-in-from-right-2 duration-200'
                : dir === 'back' ? 'animate-in fade-in slide-in-from-left-2 duration-200' : ''}`}
          >
            {isAdmin && editingKey === current.key ? (
              <CardEditor
                key={current.id}
                node={current}
                nodes={nodes}
                lang={lang}
                onSaved={onSaved}
                onDeleted={onDeleted}
                onCancel={() => setEditingKey(null)}
              />
            ) : (
              <CardView
                node={current} byKey={byKey} ctx={ctx} setCtx={setCtx} code={code} titleRef={titleRef}
                canBack={path.length > 1} onGo={go} onBack={back} onRestart={() => restart()}
                onEdit={isAdmin ? () => setEditingKey(current.key) : undefined}
                lang={lang} lbl={lbl}
              />
            )}
          </section>
          <p className="hidden text-[12px] text-slate-400 lg:block">{lbl.keys}</p>
        </main>
      </div>

      {rulesNode && (
        <RulesDialog open={rulesOpen} node={rulesNode} onClose={() => setRulesOpen(false)} lang={lang} lbl={lbl} />
      )}
    </div>
  );
}

/** « Ce que vous avez relevé » : le code saisi, la réponse choisie sur chaque carte qui porte un libellé de fait
 *  (scope, cadran, pastille), la distance. La réponse d'une carte est l'étiquette posée en la QUITTANT. */
function factsOf(
  path: string[], choices: Choice[], byKey: Map<string, TutorialNodeDto>, code: DecodedCode, ctx: Ctx, lang: Lang, lbl: Labels,
): [string, ReactNode][] {
  const out: [string, ReactNode][] = [];
  if (code.state === 'ok') {
    out.push([lbl.factCode, (
      <><code className="rounded bg-slate-100 px-1 font-mono text-[0.9em]">{code.code}</code>{' '}{GROUP_LABEL[lang][code.group]}
        {code.quadrant ? fill(lbl.quadrant, { q: code.quadrant }) : ''}</>
    )]);
  }
  path.forEach((k, i) => {
    const n = byKey.get(k);
    if (n?.card.factLabel && choices[i]) out.push([n.card.factLabel, choices[i].chip]);
  });
  // Une distance ne s'inscrit que si elle se lit : un nombre, positif ou nul. Le verdict de l'aide dit le reste.
  const metres = Number.parseFloat(ctx.distance.replace(',', '.').trim());
  if (!Number.isNaN(metres) && metres >= 0) {
    const pose = ctx.pose ? `, ${lbl[`pose${ctx.pose}` as 'pose7']}`.toLowerCase() : '';
    out.push([lbl.factDistance, `${metres} m${pose}`]);
  }
  return out;
}

// ── Le rail : les six phases, le chemin parcouru, et « Tout voir » ─────────────────────────────────────

function Rail({
  nodes, path, current, showAll, onToggleAll, onOpen, facts, lang, lbl,
}: {
  nodes: TutorialNodeDto[];
  path: string[];
  current: TutorialNodeDto;
  showAll: boolean;
  onToggleAll: () => void;
  onOpen: (key: string) => void;
  facts: [string, ReactNode][];
  lang: Lang;
  lbl: Labels;
}) {
  const cur = phaseIndex(phaseOf(current));
  const visited = new Set(path);
  return (
    <aside className="hidden lg:block" aria-label={lbl.railTitle}>
      {/* « Tout voir » déplie une soixantaine d'étapes : plus haut que l'écran, un rail collant serait coupé. Il
          défile donc pour son compte. */}
      <div className="sticky top-4 max-h-[calc(100vh-2rem)] space-y-5 overflow-y-auto pr-1">
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{lbl.railTitle}</p>
            <button type="button" onClick={onToggleAll} className="text-[11.5px] font-semibold text-brand-rose hover:underline">
              {showAll ? lbl.showPath : lbl.showAll}
            </button>
          </div>
          <ol className="space-y-0.5">
            {TUTORIAL_PHASES.map((p, i) => {
              const steps = showAll
                ? nodes.filter((n) => phaseOf(n) === p && n.card.kind !== 'rules').sort((a, b) => a.sortOrder - b.sortOrder)
                : [...new Set(path)].map((k) => nodes.find((n) => n.key === k)).filter((n): n is TutorialNodeDto => !!n && phaseOf(n) === p);
              const state = i === cur ? 'current' : path.some((k) => phaseOf(nodes.find((n) => n.key === k)) === p) ? 'done' : 'todo';
              return (
                <li key={p} className="grid grid-cols-[24px_1fr] gap-x-2.5 py-1">
                  <span className={`grid h-6 w-6 place-items-center rounded-full border-[1.5px] font-mono text-[11px] ${
                    state === 'done' ? 'border-brand-rose bg-brand-rose text-white'
                      : state === 'current' ? 'border-brand-rose text-brand-rose ring-4 ring-brand-rose/15'
                        : 'border-slate-300 bg-white text-slate-400'}`}
                  >
                    {state === 'done' ? <Check className="h-3.5 w-3.5" /> : i + 1}
                  </span>
                  <span className={`self-center text-[13.5px] font-semibold ${
                    state === 'current' ? 'text-slate-800' : state === 'done' ? 'text-slate-600' : 'text-slate-400'}`}
                  >
                    {PHASE_LABEL[lang][p]}
                  </span>
                  {steps.length > 0 && (
                    <ul className="col-start-2 mt-1 space-y-0.5">
                      {steps.map((n) => {
                        const here = n.key === current.key;
                        return (
                          <li key={n.key}>
                            <button
                              type="button"
                              onClick={() => onOpen(n.key)}
                              className={`text-left text-[12.5px] leading-snug hover:underline ${
                                here ? 'font-semibold text-brand-rose' : visited.has(n.key) ? 'text-slate-600' : 'text-slate-400'}`}
                            >
                              {shortOf(n)}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ol>
        </div>

        <div>
          <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400">{lbl.factsTitle}</p>
          {facts.length > 0 ? (
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-2.5 gap-y-1 text-[12.5px]">
              {facts.map(([k, v], i) => (
                <div key={i} className="contents">
                  <dt className="font-semibold text-slate-400">{k}</dt>
                  <dd className="min-w-0 text-slate-700">{v}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="text-[12.5px] text-slate-400">{lbl.factsEmpty}</p>
          )}
        </div>

        <div>
          <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400">{lbl.legendTitle}</p>
          <ul className="space-y-2">
            {(Object.keys(TAG_LABEL[lang]) as TutorialTag[]).map((t) => (
              <li key={t} className="flex flex-col items-start gap-0.5 text-[12px] text-slate-500">
                <TagPill tag={t} lang={lang} />
                {TAG_LABEL[lang][t][1]}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </aside>
  );
}

function MobileProgress({ phase, facts, lang, lbl }: { phase: TutorialPhase; facts: [string, ReactNode][]; lang: Lang; lbl: Labels }) {
  const cur = phaseIndex(phase);
  return (
    <div className="space-y-1.5 lg:hidden">
      <div className="grid grid-cols-6 gap-1">
        {TUTORIAL_PHASES.map((p, i) => (
          <span key={p} className={`h-1.5 rounded-full ${i <= cur ? 'bg-brand-gradient' : 'bg-slate-200'}`} />
        ))}
      </div>
      <p className="text-[13px] text-slate-600">
        {fill(lbl.stepOf, { n: cur + 1, total: TUTORIAL_PHASES.length })} · <b className="text-slate-800">{PHASE_LABEL[lang][phase]}</b>
      </p>
      {facts.length > 0 && (
        <p className="text-[12.5px] text-slate-500">
          {facts.map(([k, v], i) => <span key={i}>{i > 0 && ' · '}{k} : {v}</span>)}
        </p>
      )}
    </div>
  );
}

function Trail({
  path, choices, byKey, onJump, lbl,
}: {
  path: string[];
  choices: Choice[];
  byKey: Map<string, TutorialNodeDto>;
  onJump: (i: number) => void;
  lbl: Labels;
}) {
  if (choices.length === 0) {
    return (
      <p className="flex min-h-[32px] items-center text-[13px] text-slate-400">
        {path.length === 1 && !byKey.get(path[0])?.isRoot ? lbl.trailDirect : lbl.trailEmpty}
      </p>
    );
  }
  return (
    <nav className="flex min-h-[32px] flex-wrap items-center gap-1.5" aria-label={lbl.backTo}>
      {choices.map((c, i) => {
        const n = byKey.get(path[i]);
        return (
          <span key={i} className="flex items-center gap-1.5">
            {i > 0 && <span className="text-[13px] text-slate-300" aria-hidden="true">›</span>}
            <button
              type="button"
              onClick={() => onJump(i)}
              title={`${c.auto ? `${lbl.byCodeTitle} ` : ''}${lbl.backTo} ${n?.title ?? ''}`}
              className={`inline-flex items-center gap-1.5 rounded-full border bg-white px-2.5 py-1 text-[12.5px] text-slate-400 transition-colors hover:border-brand-rose hover:text-slate-600 ${
                c.auto ? 'border-dashed border-emerald-300' : 'border-slate-200'}`}
            >
              {n ? shortOf(n) : '?'} <b className="font-semibold text-slate-700">{c.chip}</b>
              {/* N32.7 — l'étape que le code a passée d'office : elle reste dans le fil, et se dit. */}
              {c.auto && <span className="text-[10.5px] font-bold uppercase tracking-wide text-emerald-700">{lbl.byCode}</span>}
            </button>
          </span>
        );
      })}
    </nav>
  );
}

function TagPill({ tag, lang }: { tag: TutorialTag; lang: Lang }) {
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold ${TAG_CLASS[tag]}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
      {TAG_LABEL[lang][tag][0]}
    </span>
  );
}

function Callout({ type, text, lang }: { type: string; text: ReactNode; lang: Lang }) {
  const prefix = CALLOUT_PREFIX[lang][type] ?? '';
  return (
    <div className={`max-w-[70ch] rounded-lg border px-3.5 py-2.5 text-[14px] ${CALLOUT_CLASS[type] ?? CALLOUT_CLASS.ok}`}>
      {prefix && <b className="mr-1">{prefix}</b>}
      {typeof text === 'string' ? <Rich text={text} /> : text}
    </div>
  );
}

// ── La carte ──────────────────────────────────────────────────────────────────────────────────────────

function CardView({
  node, byKey, ctx, setCtx, code, titleRef, canBack, onGo, onBack, onRestart, onEdit, lang, lbl,
}: {
  node: TutorialNodeDto;
  byKey: Map<string, TutorialNodeDto>;
  ctx: Ctx;
  setCtx: (f: (c: Ctx) => Ctx) => void;
  code: DecodedCode;
  titleRef: RefObject<HTMLHeadingElement | null>;
  canBack: boolean;
  onGo: (to: string | null | undefined, chip: string) => void;
  onBack: () => void;
  onRestart: () => void;
  /** Admins seulement : passe la carte en édition. */
  onEdit?: () => void;
  lang: Lang;
  lbl: Labels;
}) {
  const card: TutorialCard = node.card;
  const phase = phaseOf(node);
  const pi = phaseIndex(phase);
  const special = card.kind === 'code' || card.kind === 'scope' || card.kind === 'window';
  const sugg = special ? -1 : suggestedIndex(node.answers, code);
  const checks = ctx.checks[node.key] ?? {};
  const checklistDone = card.checklist.every((c) => !c.required || checks[c.id]);
  const nextOk = !!card.next?.targetKey && byKey.has(card.next.targetKey);

  return (
    <div>
      <div className="mb-2.5 flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
          {fill(lbl.stepOf, { n: pi + 1, total: TUTORIAL_PHASES.length })} · {PHASE_LABEL[lang][phase]}
        </span>
        {card.tags.map((t) => <TagPill key={t} tag={t} lang={lang} />)}
        {onEdit && (
          <Button variant="outline" size="sm" className="ml-auto h-7 text-xs" onClick={onEdit}>
            <Pencil className="mr-1.5 h-3.5 w-3.5" /> {lbl.edit}
          </Button>
        )}
      </div>
      <h2 ref={titleRef} tabIndex={-1} className="mb-2 text-[22px] font-bold leading-tight text-slate-800 outline-none sm:text-[26px]">
        <Rich text={node.title} />
      </h2>
      {card.lead && <RichBlock text={card.lead} className="mb-4 max-w-[66ch] space-y-2 text-[15px] text-slate-600" />}
      {card.points.length > 0 && (
        <ul className="mb-4 max-w-[68ch] list-disc space-y-2 pl-5 text-[15px] text-slate-700 marker:font-bold marker:text-brand-rose">
          {card.points.map((p, i) => <li key={i}><Rich text={p} /></li>)}
        </ul>
      )}
      {card.steps.length > 0 && (
        <ol className="mb-4 max-w-[68ch] list-decimal space-y-2 pl-5 text-[15px] text-slate-700 marker:font-bold marker:text-brand-rose">
          {card.steps.map((p, i) => <li key={i}><Rich text={p} /></li>)}
        </ol>
      )}
      {(card.callouts.length > 0 || sugg >= 0) && (
        <div className="mb-4 space-y-2">
          {card.callouts.map((c, i) => <Callout key={i} type={c.type} text={c.text} lang={lang} />)}
          {sugg >= 0 && code.state === 'ok' && (
            <Callout
              type="ok"
              lang={lang}
              text={fill(lbl.suggestion, {
                code: `\`${code.code}\``,
                group: GROUP_LABEL[lang][code.group],
                quadrant: code.quadrant ? fill(lbl.quadrant, { q: code.quadrant }) : '',
              })}
            />
          )}
        </div>
      )}

      {card.checklist.length > 0 && (
        <fieldset className="mb-3 max-w-[720px] space-y-2">
          {card.checklist.map((c) => (
            <label
              key={c.id}
              className={`grid cursor-pointer grid-cols-[20px_minmax(0,1fr)_auto] items-start gap-3 rounded-xl border-[1.5px] px-3.5 py-3 transition-colors ${
                checks[c.id] ? 'border-brand-rose/60 bg-white' : 'border-slate-200 bg-slate-50/60 hover:border-brand-rose/40'}`}
            >
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-brand-rose"
                checked={!!checks[c.id]}
                onChange={(e) => {
                  const on = e.target.checked;
                  setCtx((x) => ({ ...x, checks: { ...x.checks, [node.key]: { ...(x.checks[node.key] ?? {}), [c.id]: on } } }));
                }}
              />
              <span>
                <span className="block text-[14px] font-semibold text-slate-800"><Rich text={c.label} /></span>
                {c.hint && <span className="mt-0.5 block text-[13px] text-slate-500"><Rich text={c.hint} /></span>}
              </span>
              {!c.required && <span className="pt-0.5 text-[11px] font-semibold text-slate-400">{lbl.optional}</span>}
            </label>
          ))}
          {card.checklistHint && <p className="text-[13px] text-slate-500"><Rich text={card.checklistHint} /></p>}
        </fieldset>
      )}

      {card.distanceHelper && <DistanceHelper ctx={ctx} setCtx={setCtx} code={code} lbl={lbl} />}

      {card.kind === 'code' ? (
        <CodeWidget node={node} ctx={ctx} setCtx={setCtx} code={code} byKey={byKey} onGo={onGo} lang={lang} lbl={lbl} />
      ) : card.kind === 'scope' ? (
        <ScopeWidget node={node} ctx={ctx} setCtx={setCtx} byKey={byKey} onGo={onGo} lbl={lbl} />
      ) : card.kind === 'window' && card.window ? (
        <div className="mt-1 max-w-[560px] overflow-hidden rounded-2xl border border-slate-300 bg-white shadow-sm" role="group">
          <div className="flex justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3.5 py-2 text-[12px] font-semibold text-slate-400">
            <span>{card.window.bar}</span>
          </div>
          <div className="p-4">
            <h3 className="mb-1.5 text-[18px] font-bold text-slate-800">{card.window.title}</h3>
            <p className="mb-4 text-[14px] text-slate-600">{card.window.text}</p>
            <div className="flex flex-wrap justify-end gap-2">
              {node.answers.map((a, i) => (
                <Button
                  key={i}
                  data-k={i + 1}
                  variant={a.primary ? 'default' : 'outline'}
                  className={a.primary ? 'bg-brand-gradient text-white hover:opacity-90' : ''}
                  disabled={!a.targetKey || !byKey.has(a.targetKey)}
                  onClick={() => onGo(a.targetKey, chipOf(a))}
                >
                  <Rich text={a.label} />
                </Button>
              ))}
            </div>
          </div>
        </div>
      ) : node.answers.length > 0 ? (
        <Answers answers={node.answers} byKey={byKey} suggested={sugg} onGo={onGo} numbered lbl={lbl} />
      ) : null}

      {card.more && (
        <details className="mt-5 max-w-[72ch] border-t border-slate-100 pt-3">
          <summary className="cursor-pointer text-[14px] font-semibold text-brand-rose">{card.moreLabel || lbl.more}</summary>
          <RichBlock text={card.more} className="mt-2.5 space-y-2.5 text-[14.5px] text-slate-600" />
        </details>
      )}

      {node.comment && <CommentBox text={node.comment} lbl={lbl} />}

      {node.reference && (
        <p className={`mt-4 max-w-[72ch] rounded-lg border px-3 py-2 text-[11.5px] ${
          card.tags.includes('todo') ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-slate-200 bg-white text-slate-500'}`}
        >
          <span className="font-semibold">{lbl.reference} :</span> {node.reference}
        </p>
      )}

      <div className="mt-6 flex flex-wrap items-center justify-between gap-2.5">
        <div>
          {canBack && (
            <Button variant="ghost" onClick={onBack}>
              <ArrowLeft className="mr-1.5 h-4 w-4" /> {lbl.back}
            </Button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {card.next && (
            <Button
              className="bg-brand-gradient text-white hover:opacity-90"
              disabled={!checklistDone || !nextOk}
              title={!nextOk ? (card.next.targetKey ? lbl.missingTarget : lbl.notWired) : undefined}
              onClick={() => onGo(card.next?.targetKey, card.next?.chip || card.next?.label || '')}
            >
              <Rich text={card.next.label} /> <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          )}
          {card.kind === 'end' && (
            <Button className="bg-brand-gradient text-white hover:opacity-90" onClick={onRestart}>{lbl.nextTicket}</Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Answers({
  answers, byKey, suggested, onGo, numbered, lbl,
}: {
  answers: TutorialAnswer[];
  byKey: Map<string, TutorialNodeDto>;
  suggested: number;
  onGo: (to: string | null | undefined, chip: string) => void;
  numbered: boolean;
  lbl: Labels;
}) {
  return (
    <div className="mt-1 flex max-w-[720px] flex-col gap-2">
      {answers.map((a, i) => {
        const wired = !!a.targetKey && byKey.has(a.targetKey);
        const isS = i === suggested;
        return (
          <button
            key={i}
            type="button"
            data-k={numbered && i < 9 ? i + 1 : undefined}
            disabled={!wired}
            onClick={() => onGo(a.targetKey, chipOf(a))}
            className={`grid grid-cols-[26px_1fr] items-start gap-3 rounded-xl border-[1.5px] px-3.5 py-3 text-left transition-all ${
              !wired ? 'cursor-not-allowed border-dashed border-slate-200 bg-slate-50 opacity-70'
                : isS ? 'border-emerald-300 bg-emerald-50 hover:border-brand-rose'
                  : 'border-slate-200 bg-slate-50/60 hover:translate-x-0.5 hover:border-brand-rose hover:bg-white'}`}
          >
            <span className="grid h-6 w-6 place-items-center rounded-md border border-slate-200 bg-white font-mono text-[12px] text-slate-400" aria-hidden="true">
              {numbered && i < 9 ? i + 1 : '·'}
            </span>
            <span>
              <span className="text-[14.5px] font-semibold text-slate-800"><Rich text={a.label} /></span>
              {isS && <span className="ml-2 text-[10.5px] font-bold uppercase tracking-wide text-emerald-700">{lbl.suggested}</span>}
              {a.hint && <span className="mt-0.5 block text-[13px] font-medium text-slate-500"><Rich text={a.hint} /></span>}
              {!wired && (
                <span className="mt-0.5 block text-[12px] italic text-slate-400">{a.targetKey ? lbl.missingTarget : lbl.notWired}</span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function CodeWidget({
  node, ctx, setCtx, code, byKey, onGo, lang, lbl,
}: {
  node: TutorialNodeDto;
  ctx: Ctx;
  setCtx: (f: (c: Ctx) => Ctx) => void;
  code: DecodedCode;
  byKey: Map<string, TutorialNodeDto>;
  onGo: (to: string | null | undefined, chip: string) => void;
  lang: Lang;
  lbl: Labels;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const routeIndex = code.state === 'ok' ? node.answers.findIndex((a) => matches(
    a.match?.groups?.length ? { groups: a.match.groups } : null, code,
  )) : -1;
  const route = routeIndex >= 0 ? node.answers[routeIndex] : null;
  const routeOk = !!route?.targetKey && byKey.has(route.targetKey);
  const goRoute = () => {
    if (code.state !== 'ok' || !route || !routeOk) return;
    onGo(route.targetKey, `${code.code} · ${GROUP_LABEL[lang][code.group]}`);
  };

  return (
    <div>
      <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-slate-400" htmlFor="tuto-code">{lbl.codeLabel}</label>
      <div className="flex flex-wrap items-center gap-2.5">
        <input
          id="tuto-code"
          ref={inputRef}
          inputMode="numeric"
          autoComplete="off"
          maxLength={4}
          placeholder="8015"
          value={ctx.code}
          onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 4); setCtx((x) => ({ ...x, code: v })); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); goRoute(); } }}
          className="w-[6.4em] rounded-xl border-[1.5px] border-slate-300 bg-slate-50 px-3 py-2.5 font-mono text-[26px] tracking-[0.16em] text-slate-800 outline-none focus:border-brand-rose focus:ring-2 focus:ring-brand-rose/30"
        />
        <Button className="bg-brand-gradient text-white hover:opacity-90" disabled={!routeOk} onClick={goRoute}>
          {lbl.continue} <ArrowRight className="ml-1.5 h-4 w-4" />
        </Button>
      </div>
      {node.card.examples.length > 0 && (
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5 text-[13px] text-slate-400">
          {lbl.examples}
          {node.card.examples.map((x) => (
            <button
              key={x}
              type="button"
              onClick={() => { setCtx((c) => ({ ...c, code: x })); inputRef.current?.focus(); }}
              className="rounded-md border border-slate-200 bg-white px-2 py-1 font-mono text-[12.5px] text-slate-600 hover:border-brand-rose hover:text-slate-800"
            >
              {x}
            </button>
          ))}
        </div>
      )}
      <div className="mt-4 max-w-[760px] space-y-2.5">
        <DecodePanel code={code} lang={lang} lbl={lbl} />
        {code.state === 'ok' && !routeOk && <p className="text-[13px] text-slate-500">{lbl.noRoute}</p>}
      </div>
      {node.answers.length > 0 && (
        <details className="mt-4 max-w-[72ch] border-t border-slate-100 pt-3">
          <summary className="cursor-pointer text-[14px] font-semibold text-brand-rose">{lbl.noCode}</summary>
          <div className="mt-2.5">
            <Answers answers={node.answers} byKey={byKey} suggested={routeIndex} onGo={onGo} numbered={false} lbl={lbl} />
          </div>
        </details>
      )}
    </div>
  );
}

function DecodePanel({ code, lang, lbl }: { code: DecodedCode; lang: Lang; lbl: Labels }) {
  if (code.state === 'empty') return <Verdict cls="neutral" text={lbl.decodeEmpty} />;
  if (code.state === 'partial') return <Verdict cls="neutral" text={lbl.decodePartial} />;
  if (code.state === 'unknown') return <Verdict cls="bad" text={fill(lbl.decodeUnknown, { code: code.code })} />;
  const seg = (digits: string, cap: string, val: string, bad = false) => (
    <div className="flex min-w-0 flex-col gap-0.5 rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2.5">
      <span className={`font-mono text-[22px] leading-tight ${bad ? 'text-rose-700' : 'text-brand-rose'}`}>{digits}</span>
      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{cap}</span>
      <span className="text-[13.5px] font-semibold text-slate-800">{val}</span>
    </div>
  );
  const [cls, text] = GROUP_VERDICT[lang][code.group];
  return (
    <>
      {code.shape === 'composite' && code.digits ? (
        <div className="grid gap-2 sm:grid-cols-[0.8fr_1.6fr_0.9fr]">
          {seg(code.digits.pose, lbl.segPose, code.digits.poseLabel)}
          {seg(code.digits.net, lbl.segNet, code.digits.netLabel)}
          {seg(code.digits.status, lbl.segStatus, code.digits.statusLabel)}
        </div>
      ) : code.shape === 'flat' ? (
        seg(String(code.code), lbl.flatSeries, `« ${code.label} »`)
      ) : (
        seg(String(code.code), code.family ?? '', `« ${code.reason ?? lbl.blockedUnknown} »`, true)
      )}
      <Verdict cls={cls} text={text} />
    </>
  );
}

function Verdict({ cls, text }: { cls: string; text: string }) {
  return <p className={`max-w-[760px] rounded-lg border px-3 py-2 text-[14px] font-semibold ${VERDICT_CLASS[cls] ?? VERDICT_CLASS.neutral}`}>{text}</p>;
}

function ScopeWidget({
  node, ctx, setCtx, byKey, onGo, lbl,
}: {
  node: TutorialNodeDto;
  ctx: Ctx;
  setCtx: (f: (c: Ctx) => Ctx) => void;
  byKey: Map<string, TutorialNodeDto>;
  onGo: (to: string | null | undefined, chip: string) => void;
  lbl: Labels;
}) {
  const outcome = scopeOutcome(ctx.scope);
  const i = node.answers.findIndex((a) => matches(a.match?.scope?.length ? { scope: a.match.scope } : null, { state: 'empty' }, outcome));
  const a = i >= 0 ? node.answers[i] : null;
  const ok = !!a?.targetKey && byKey.has(a.targetKey);
  const items: { k: keyof ScopeState; l: string; h: string }[] = [
    { k: 'v2', l: lbl.scopeV2, h: lbl.scopeV2Hint },
    { k: 'v15', l: lbl.scopeV15, h: lbl.scopeV15Hint },
    { k: 'oos', l: lbl.scopeOos, h: lbl.scopeOosHint },
  ];
  const cls = outcome === 'oos' ? 'bad' : outcome === 'none' ? 'todo' : 'ok';
  return (
    <div>
      <fieldset className="mb-3 max-w-[720px] space-y-2">
        {items.map((it) => (
          <label
            key={it.k}
            className={`grid cursor-pointer grid-cols-[20px_minmax(0,1fr)] items-start gap-3 rounded-xl border-[1.5px] px-3.5 py-3 transition-colors ${
              ctx.scope[it.k] ? 'border-brand-rose/60 bg-white' : 'border-slate-200 bg-slate-50/60 hover:border-brand-rose/40'}`}
          >
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-brand-rose"
              checked={ctx.scope[it.k]}
              onChange={(e) => { const on = e.target.checked; setCtx((x) => ({ ...x, scope: { ...x.scope, [it.k]: on } })); }}
            />
            <span>
              <span className="block text-[14px] font-semibold text-slate-800">{it.l}</span>
              <span className="mt-0.5 block text-[13px] text-slate-500">{it.h}</span>
            </span>
          </label>
        ))}
      </fieldset>
      {a ? (
        <Verdict cls={cls} text={`${a.label.replace(/\*\*|`/g, '')}${a.hint ? ` — ${a.hint.replace(/\*\*|`/g, '')}` : ''}`} />
      ) : (
        <p className="text-[13px] text-slate-500">{lbl.noRoute}</p>
      )}
      <div className="mt-3.5">
        <Button className="bg-brand-gradient text-white hover:opacity-90" disabled={!ok} onClick={() => a && onGo(a.targetKey, chipOf(a))}>
          {lbl.continue} <ArrowRight className="ml-1.5 h-4 w-4" />
        </Button>
      </div>
      {node.answers.length > 0 && (
        <details className="mt-4 max-w-[72ch] border-t border-slate-100 pt-3">
          <summary className="cursor-pointer text-[14px] font-semibold text-brand-rose">{lbl.chooseMyself}</summary>
          <div className="mt-2.5">
            <Answers answers={node.answers} byKey={byKey} suggested={i} onGo={onGo} numbered={false} lbl={lbl} />
          </div>
        </details>
      )}
    </div>
  );
}

function DistanceHelper({ ctx, setCtx, code, lbl }: { ctx: Ctx; setCtx: (f: (c: Ctx) => Ctx) => void; code: DecodedCode; lbl: Labels }) {
  // Le type de pose se déduit du code saisi plus tôt quand il est composé : on ne redemande pas ce qu'on sait.
  useEffect(() => {
    if (!ctx.pose && code.state === 'ok' && code.shape === 'composite' && code.digits) {
      const p = code.digits.pose;
      if (p === '7' || p === '8' || p === '9') setCtx((x) => ({ ...x, pose: p }));
    }
  }, [ctx.pose, code, setCtx]);
  const [cls, text] = distanceVerdict(ctx, lbl);
  return (
    <div className="mb-3 flex max-w-[720px] flex-col gap-2.5 rounded-xl border-[1.5px] border-dashed border-slate-300 p-3.5">
      <p className="font-bold text-slate-800">{lbl.distanceTitle}</p>
      <div className="flex flex-wrap gap-1.5" role="radiogroup">
        {(['7', '8', '9'] as const).map((p) => (
          <label
            key={p}
            className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border-[1.5px] px-3 py-1.5 text-[13.5px] font-semibold ${
              ctx.pose === p ? 'border-brand-rose bg-white' : 'border-slate-200 bg-slate-50/60'}`}
          >
            <input type="radio" name="tuto-pose" className="accent-brand-rose" checked={ctx.pose === p} onChange={() => setCtx((x) => ({ ...x, pose: p }))} />
            {lbl[`pose${p}`]} <code className="rounded bg-slate-100 px-1 font-mono text-[0.85em]">{p}xxx</code>
          </label>
        ))}
      </div>
      <div>
        <label htmlFor="tuto-distance" className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-slate-400">{lbl.distanceLabel}</label>
        <div className="flex items-center gap-2">
          <input
            id="tuto-distance"
            inputMode="decimal"
            autoComplete="off"
            placeholder="24"
            value={ctx.distance}
            onChange={(e) => { const v = e.target.value; setCtx((x) => ({ ...x, distance: v })); }}
            className="w-[5.5em] rounded-xl border-[1.5px] border-slate-300 bg-slate-50 px-2.5 py-2 font-mono text-[18px] text-slate-800 outline-none focus:border-brand-rose focus:ring-2 focus:ring-brand-rose/30"
          />
          <span className="font-semibold text-slate-600">m</span>
        </div>
      </div>
      <Verdict cls={cls} text={text} />
    </div>
  );
}

function CommentBox({ text, lbl }: { text: string; lbl: Labels }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="mt-4 max-w-[72ch] rounded-xl border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{lbl.comment}</p>
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          onClick={() => {
            // navigator.clipboard peut être refusé (page non sécurisée, permission) : l'écran ne ment pas sur un
            // succès qui n'a pas eu lieu.
            navigator.clipboard.writeText(text).then(
              () => { setCopied(true); setTimeout(() => setCopied(false), 1500); },
              () => toast.error(lbl.copyError),
            );
          }}
        >
          {copied ? <Check className="mr-1.5 h-3.5 w-3.5" /> : <Copy className="mr-1.5 h-3.5 w-3.5" />}
          {copied ? lbl.copied : lbl.copy}
        </Button>
      </div>
      <div className="mt-1.5 whitespace-pre-line text-[13.5px] leading-relaxed text-slate-700">{text}</div>
    </div>
  );
}

// ── La fenêtre « Règles de capacité » : une carte hors parcours, modifiable comme les autres ───────────

function RulesDialog({ open, node, onClose, lang, lbl }: { open: boolean; node: TutorialNodeDto; onClose: () => void; lang: Lang; lbl: Labels }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  const card = node.card;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-[2px]" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="tuto-rules-title"
        onClick={(e) => e.stopPropagation()}
        className="max-h-[calc(100vh-48px)] w-full max-w-[780px] overflow-y-auto rounded-2xl bg-white shadow-2xl"
      >
        <div className="h-1 bg-brand-gradient" aria-hidden="true" />
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div>
            <h2 id="tuto-rules-title" className="text-[19px] font-bold text-slate-800">{node.title}</h2>
            {card.lead && <p className="mt-0.5 text-[13px] text-slate-500"><Rich text={card.lead} /></p>}
          </div>
          <Button variant="outline" size="sm" onClick={onClose} aria-label={lbl.close}><X className="h-4 w-4" /></Button>
        </div>
        <div className="space-y-3 px-5 py-4">
          {card.points.length > 0 && (
            <ul className="divide-y divide-slate-100 text-[14px] text-slate-700">
              {card.points.map((p, i) => <li key={i} className="py-2.5"><Rich text={p} /></li>)}
            </ul>
          )}
          {card.callouts.map((c, i) => <Callout key={i} type={c.type} text={c.text} lang={lang} />)}
          {card.more && <RichBlock text={card.more} className="space-y-2 text-[13.5px] text-slate-600" />}
          {node.reference && <p className="text-[12px] text-slate-400"><span className="font-semibold">{lbl.reference} :</span> {node.reference}</p>}
        </div>
      </div>
    </div>,
    document.body,
  );
}
